import { FC } from "react";
import { useTranslation } from "react-i18next";
import { Box, CircularProgress, Typography, useTheme } from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { FormikInputField } from "@vilocnv/allsetra-core";
import { IVatCheckStatus, VatCheckState } from "app/data/types/vatTypes";

type Severity = "error" | "success" | "info";

const SEVERITY: Partial<Record<VatCheckState, Severity>> = {
  checking: "info",
  valid: "success",
  unsupported: "info",
  invalid: "error",
  unavailable: "error",
  malformed: "error",
  mismatch: "error"
};

const SEVERITY_COLOR: Record<Severity, string> = {
  error: "error.main",
  success: "success.main",
  info: "text.secondary"
};

const NOT_CHECKED: IVatCheckStatus = {
  state: "not-checked",
  message: null,
  checkedValue: "",
  checkedCountry: null
};

const StatusIcon: FC<{ state: VatCheckState }> = ({ state }) => {
  if (state === "checking") return <CircularProgress size={18} />;
  if (state === "valid") {
    return <CheckCircleOutlineIcon fontSize="small" color="success" />;
  }
  if (state === "unsupported") {
    return <InfoOutlinedIcon fontSize="small" color="info" />;
  }
  return null;
};

interface Props {
  name: string;
  label: string;
  placeholder?: string;
  // Backend check state; omitted where only the local rules apply.
  status?: IVatCheckStatus;
  // Runs the pending check right away (`useVatValidation().flush`).
  onBlur?: () => void;
  required?: boolean;
  // Account Settings shows the local rules while typing; the activation form
  // waits for the field to be touched, like its other fields.
  displayErrorBeforeTouch?: boolean;
}

// The local rules (required, format, prefix) come from the form's Yup schema
// and are shown as the field error; the backend answer is the line below it.
const VatNumberField: FC<Props> = ({
  name,
  label,
  placeholder,
  status = NOT_CHECKED,
  onBlur,
  required = false,
  displayErrorBeforeTouch = true
}) => {
  const theme = useTheme();
  const { t } = useTranslation("formFieldsTranslation");
  const severity = SEVERITY[status.state];
  const hasIcon = ["checking", "valid", "unsupported"].includes(status.state);

  return (
    <Box>
      <FormikInputField
        theme={theme}
        label={label}
        placeholder={placeholder}
        name={name}
        fullWidth
        required={required}
        autoComplete="off"
        debounceDelay={0}
        displayErrorBeforeTouch={displayErrorBeforeTouch}
        endIcon={hasIcon ? <StatusIcon state={status.state} /> : undefined}
        onBlur={onBlur}
      />
      {status.message && severity && (
        <Typography
          variant="caption"
          role="status"
          aria-live="polite"
          data-testid="vat-number-status"
          data-severity={severity}
          sx={{ display: "block", mt: 0.5, color: SEVERITY_COLOR[severity] }}
        >
          {t(status.message)}
        </Typography>
      )}
    </Box>
  );
};

export default VatNumberField;
